import { InngestMiddleware } from "inngest";
import { createLogger } from "@/lib/logger";
import { parseHumanReadableDuration } from "@/lib/time";

/**
 * Logs how long it took for an Inngest event to start executing after it was fired.
 *
 * Ideally, we'd use inngest's onRunStart hook, but that'd involve us bumping our inngest client to v4
 * The breaking changes between v3 to v4 are that the middleware system was completely rewritten.
 * At this time, I don't feel like resolving those breaking changes, so we'll make v3 work.
 *
 * This middleware is heavily based on the example middleware for logging provided by inngest.
 * See: https://www.inngest.com/docs/reference/typescript/v3/middleware/examples#logging
 */
export function eventSchedulingLatencyMiddleware() {
  const logger = createLogger("inngest/event-scheduling-latency");

  return new InngestMiddleware({
    name: "vibrant/event-scheduling-latency",
    init() {
      return {
        onFunctionRun({ ctx, fn, steps }) {
          return {
            transformInput() {
              try {
                if (steps.length === 0) {
                  // we try to approximate the first run by only logging if the steps length is currently 0
                  // this is not perfect, notably it'll skip retries dependent on whether a previous step was able to complete successfully
                  logEventSchedulingLatency(logger, ctx, fn);
                }
              } catch (error) {
                // at no point should a logging failure ever disrupt actually serving of inngest functions
                logger.error("middleware failed; latency logs not exported", { error });
              }

              return {};
            },
          };
        },
      };
    },
  });
}

function logEventSchedulingLatency(
  logger: ReturnType<typeof createLogger>,
  ctx: {
    runId?: string;
    attempt?: number;
    name?: string;
    event?: { name?: string; ts?: number; data?: { lane?: unknown } };
  },
  fn: {
    opts: {
      rateLimit?: {
        period: string;
      };
    };
  },
): void {
  const ts = ctx.event?.ts;
  if (typeof ts !== "number") {
    logger.warn("No event ts; cannot compute scheduling latency");
    return;
  }
  const period = fn.opts.rateLimit?.period;
  const delayMs = period ? parseHumanReadableDuration(period) : null;
  const elapsedMs = Date.now() - (ts + (delayMs ?? 0));
  // document parsing concurrency lane - lets queue wait be split by interactive vs bulk
  const lane = ctx.event?.data?.lane;
  logger.info("Run started after event", {
    ...(typeof lane === "string" ? { lane } : {}),
    elapsedMs,
    runId: ctx.runId,
    attempt: ctx.attempt,
    functionName: ctx.name,
    eventName: ctx.event?.name,
  });
}
